/**
 * Form model for creating new camera groups
 */
export class NewCameraGroupForm {
  constructor(user: any) {
    this._serverId = user.serverId;
    this.CompanyId = this._serverId + user.customerId;
    this.timeZone = user.timezone;
  }
  // Avoid the underscore "_" naming convention for those private property members, which are going to be sent to the API
  private AccessMode: AccessMode | undefined = undefined;
  private LocationId: string = "";
  private OrigonId: string = "";
  private readonly CompanyId: string;
  private readonly _serverId: string;
  readonly id: string;
  readonly type: string = "cameraGroup";
  readonly version: number = 1;
  readonly system: string = "production";
  readonly timeZone: string;

  userGroups: string[] = [];
  cameras: VideoServerCamera[] = [];
  displayName: string;
  address: string;
  postOffice: string;
  postCode: string;
  deviceType: DeviceType = null;
  isPaused?: boolean;

  get accessMode(): AccessMode | undefined {
    return this.AccessMode;
  }

  set accessMode(mode: AccessMode) {
    if (Object.values(AccessMode).includes(mode as AccessMode)) {
      this.AccessMode = mode as AccessMode;
    } else {
      this.AccessMode = undefined;
    }
  }

  get locationId(): string {
    return this.LocationId.substring(8);
  }

  set locationId(id: string) {
    this.LocationId = id ? this._serverId + id : "";
  }

  get origonId(): string {
    return this.OrigonId;
  }

  set origonId(id: string) {
    this.OrigonId = id;
  }

  get getCompanyId(): string {
    return this.CompanyId.substring(8);
  }

  /**
   * Validate form data
   * @returns true if valid, else false
   */
  isValid(): boolean {
    if (
      this.LocationId &&
      this.displayName &&
      this.cameras?.length > 0
    ) {
      return true;
    }
    return false;
  }
}

/**
 * Form model for modifying existing camera group
 */
export class CameraGroupEditForm {
  constructor(cameraGroupToEdit: ICameraGroup) {
    Object.assign(this, cameraGroupToEdit); // Deep copy the immediate members of an object
    this.cameras = structuredClone(this.cameras); // Deep copy nested camera objects
    this._serverId = cameraGroupToEdit.companyId.substring(0, 8);
  }
  // Avoid the underscore "_" naming convention for those private property members, which are going to be sent to the API
  private AccessMode: AccessMode | undefined;
  private LocationId: string = "";
  private OrigonId: string = "";
  private readonly CompanyId: string;
  private readonly _serverId: string = "";
  readonly type: string;
  readonly version: number;
  readonly system: string;
  readonly id: string;
  readonly timeZone: string;

  userGroups: string[] = [];
  cameras: VideoServerCamera[] = [];
  displayName: string;
  address: string;
  postOffice: string;
  postCode: string;
  deviceType: DeviceType;
  isPaused?: boolean; 
  pauseStart?: string; 
  pauseEnd?: string; 

  get accessMode(): AccessMode | undefined {
    return this.AccessMode;
  }

  set accessMode(mode: AccessMode) {
    if (Object.values(AccessMode).includes(mode as AccessMode)) {
      this.AccessMode = mode as AccessMode;
    } else {
      this.AccessMode = undefined;
    }
  }

  get origonId(): string {
    return this.OrigonId;
  }

  set origonId(id: string) {
    this.OrigonId = id;
  }

  get locationId(): string {
    return this.LocationId.substring(8);
  }

  set locationId(id: string) {
    this.LocationId = id ? this._serverId + id : "";
  }

  get getCompanyId(): string {
    return this.CompanyId.substring(8);
  }

  /**
   * Validate form data
   * @returns true if valid, else false
   */
  isValid(): boolean {
    if (
      this.LocationId &&
      this.displayName &&
      this.cameras?.length > 0
    ) {
      return true;
    }
    return false;
  }
}

export interface ICameraGroup {
  id: string;
  accessMode: AccessMode | undefined;
  cameras: VideoServerCamera[];
  userGroups: string[];
  companyId: string;
  locationId: string;
  origonId: string;
  address: string;
  postOffice: string;
  postCode: string;
  displayName: string;
  timeZone: string;
  type: string;
  version: number;
  system: string;
  deviceType: DeviceType | undefined;
  isPaused?: boolean; // Toggle state for pause functionality
  pauseStart?: string; 
  pauseEnd?: string;
}

export class VideoServerCamera {
  readonly deviceId: string = undefined; // GUID
  discoveryName: string;
  displayName: string;
  anonymization: boolean;
  toggleAnonymization: boolean;
  restingPositionEnabled: boolean;
  motionDetection: {
    enabled: boolean;
    alertVisit: boolean; // Need to check property name
    snapshot: boolean;
    videoclip: boolean;
  };
}

export class VisitOnAlertConfig implements IVisitConfiguration {
  type: string = "alertsForCameraGroup";
  readonly version: number = 1;
  readonly system: string = "production";
  readonly id: string;

  alertSources: { deviceID: string }[] = [];
  eventsToTrig?: AlarmTypeKey[];
  doVisitByAlarmType?: boolean;
  options: IVisitOnAlertOptions = { visitTimeMinutes: 10, visitGraceTimeMinutes: 0 };
}

export interface IVisitConfiguration {
  id: string;
  type: string;
  version: number;
  system: string;
  alertSources: { deviceID: string }[];
  options: IVisitOnAlertOptions;
  eventsToTrig?: AlarmTypeKey[];
  doVisitByAlarmType?: boolean;
}

export type AlarmTypeKey = {
  alarmGroup: number;
  alarmNode: number;
  devicePrefixClass: string;
};

export const NIGHTOWL_SUPERVISION_RESUMED: AlarmTypeKey = {
  devicePrefixClass: "34-4",
  alarmGroup: 2,
  alarmNode: 16,
};

export const NIGHTOWL_SUPERVISION_PAUSED: AlarmTypeKey = {
  devicePrefixClass: "34-4",
  alarmGroup: 2,
  alarmNode: 10,
};

export const NIGHTOWL_DIGITAL_SUPERVISION: AlarmTypeKey = {
  devicePrefixClass: "34-4",
  alarmGroup: 2,
  alarmNode: 13,
};

export interface IVisitOnAlertOptions {
  visitTimeMinutes: number;
  visitGraceTimeMinutes: number;
}

export enum AccessMode {
  Always = "always",
  VisitOnAlert = "visitOnAlert",
}

export const accessModeTranslations = new Map<string, string>([
  [AccessMode.Always, "ACCESS_MODE_ALWAYS"],
  [AccessMode.VisitOnAlert, "VISIT_ON_ALERT"],
  ["", "SCHEDULED_VISIT"],
  [undefined, "SCHEDULED_VISIT"],
  [null, "SCHEDULED_VISIT"],
]);

export enum VisitTimeOption {
  visitTimeMinutes,
  visitGraceTimeMinutes,
}

export enum DeviceType {
  Camera = "Camera",
  Nightowl = "Nightowl",
}
